import { Tooltip } from "@mantine/core";
import { t } from "i18next";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { setErrorsFromResponseData } from "../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { DateInput } from "../../../../mantine/dates/DateInput/DateInput";
import { useToast } from "../../../../mantine/notifications/useToast";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import type {
  ExpressOnboardingGenerator,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingMutations } from "../hooks/useExpressOnboardingMutations";

function getExpressOnboardingTableEegInbetriebnahmeColumn(
  getError: GetErrorFn,
  setError: SetErrorFn
): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "eeg_inbetriebnahme",
    accessorKey: "eeg_inbetriebnahme",
    header: "EEG-Inbetriebnahmedatum",
    enableGlobalFilter: false,
    Cell: ({ cell, row }) => {
      const value = backendDateOrDateTimeToLuxonDateTimeOrNull(
        cell.getValue<ExpressOnboardingGenerator["eeg_inbetriebnahme"]>()
      );

      const { expressOnboardingGeneratorSave, pendingGeneratorIds } =
        useExpressOnboardingMutations();

      const { showErrorToast } = useToast();

      const rowIsSaving = pendingGeneratorIds.has(row.original.id);

      const cantEditReason = row.original.uneditable_fields?.eeg_inbetriebnahme;

      return (
        <Tooltip disabled={!cantEditReason} label={cantEditReason}>
          <DateInput
            clearable={false}
            defaultValue={value ?? undefined}
            disabled={!!cantEditReason || rowIsSaving}
            error={getError(row.original.id, "eeg_inbetriebnahme")}
            loading={rowIsSaving}
            onBlur={(event) => {
              const currentValue = backendDateOrDateTimeToLuxonDateTimeOrNull(
                event.target.value
              );

              const backendValue =
                luxonDateTimeToBackendDateOrDateTimeOrNull(currentValue);
              const hasChange =
                backendValue !==
                luxonDateTimeToBackendDateOrDateTimeOrNull(value);
              if (!hasChange) {
                return;
              }

              expressOnboardingGeneratorSave
                .mutateAsync({
                  id: row.original.id,
                  eeg_inbetriebnahme: backendValue
                })
                .catch((error) => {
                  setErrorsFromResponseData(
                    showErrorToast,
                    error,
                    row.original,
                    (field: string, err: { message?: string }) =>
                      setError(row.original.id, {
                        [field]: err?.message ?? t("errors.UnknownError")
                      }),
                    t("errors.UnknownError")
                  );
                });
            }}
          />
        </Tooltip>
      );
    }
  };
}

export { getExpressOnboardingTableEegInbetriebnahmeColumn };
